import { useQueryClient } from '@tanstack/react-query';
import { Headset, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '@/api/http-client';
import {
  getChamadosAdminControllerListarQueryKey,
  getChamadosAdminControllerResumoQueryKey,
  useChamadosAdminControllerAssumir,
  useChamadosAdminControllerListar,
  useChamadosAdminControllerResumo,
} from '@/api/generated/admin-suporte/admin-suporte';
import {
  CLASSE_STATUS,
  CLASSE_URGENCIA,
  formatarRelativo,
  ROTULO_STATUS_ADMIN,
  ROTULO_URGENCIA,
  URGENCIAS,
} from '@/features/suporte/rotulos';
import { INTERVALO_BADGE_MS, INTERVALO_LISTA_MS } from '@/features/suporte/suporte.constants';
import { EstadoCarregando, EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { cn } from '@/shared/lib/cn';
import { rotaDoChamadoAdmin } from '@/shared/lib/rotas';
import { Button } from '@/shared/ui/button';
import { Paginacao } from '@/shared/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { toast } from '@/shared/ui/sonner';

const TODAS = 'TODAS';
type Aba = 'fila' | 'meus' | 'todos';

/** Fila de atendimento — cross-tenant, chamados de todos os ambientes. */
export function FilaDeChamadosPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [aba, setAba] = useState<Aba>('fila');
  const [urgencia, setUrgencia] = useState<string>(TODAS);
  const [pagina, setPagina] = useState(1);
  const [assumindoId, setAssumindoId] = useState<string | null>(null);

  const { data: resumo } = useChamadosAdminControllerResumo({
    query: { refetchInterval: INTERVALO_BADGE_MS },
  });

  const { data, isPending, isError, refetch } = useChamadosAdminControllerListar(
    {
      pagina,
      porPagina: 20,
      // `abertoEm asc` é o gatilho da ordenação de fila do backend (urgência
      // desc + mais antigo primeiro). Ver `ChamadosAdminService.listar`.
      ordenarPor: 'abertoEm',
      ordem: 'asc',
      ...(aba === 'fila' ? { apenasNaFila: true } : {}),
      ...(aba === 'meus' ? { apenasMeus: true } : {}),
      ...(urgencia !== TODAS ? { urgencia: urgencia as never } : {}),
    },
    { query: { refetchInterval: INTERVALO_LISTA_MS } },
  );

  const assumir = useChamadosAdminControllerAssumir();

  async function aoAssumir(id: string) {
    setAssumindoId(id);
    try {
      await assumir.mutateAsync({ id });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getChamadosAdminControllerListarQueryKey() }),
        queryClient.invalidateQueries({ queryKey: getChamadosAdminControllerResumoQueryKey() }),
      ]);
      navigate(rotaDoChamadoAdmin(id));
    } catch (e) {
      // 409 é o caso normal de corrida, não uma falha: alguém clicou primeiro.
      // Revalidar tira a linha da fila sem a pessoa precisar recarregar.
      toast.error(
        e instanceof ApiError ? e.message : 'Não foi possível assumir o chamado.',
      );
      await queryClient.invalidateQueries({ queryKey: getChamadosAdminControllerListarQueryKey() });
    } finally {
      setAssumindoId(null);
    }
  }

  return (
    <div className="space-y-6">
      {/*
        `abertos` e `naoLidas` são MÉTRICAS DIFERENTES e por isso aparecem
        separadas: chamado na fila não tem participante atendente, logo tem
        sempre 0 não lidas. Um número só diria "nenhuma novidade" com cinco
        chamados esperando alguém pegar.
      */}
      <div className="grid gap-3 sm:grid-cols-4">
        <Cartao rotulo="Na fila" valor={resumo?.abertos} destaque />
        <Cartao rotulo="Em andamento" valor={resumo?.emAndamento} />
        <Cartao rotulo="Comigo" valor={resumo?.meus} />
        <Cartao rotulo="Sem ler" valor={resumo?.naoLidas} />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Tabs
          value={aba}
          onValueChange={(v) => {
            setAba(v as Aba);
            setPagina(1);
          }}
        >
          <TabsList>
            <TabsTrigger value="fila">Fila</TabsTrigger>
            <TabsTrigger value="meus">Comigo</TabsTrigger>
            <TabsTrigger value="todos">Todos</TabsTrigger>
          </TabsList>
        </Tabs>

        <Select
          value={urgencia}
          onValueChange={(v) => {
            setUrgencia(v);
            setPagina(1);
          }}
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Urgência" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TODAS}>Todas as urgências</SelectItem>
            {URGENCIAS.map((u) => (
              <SelectItem key={u} value={u}>
                {ROTULO_URGENCIA[u]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isPending ? (
        <EstadoCarregando rotulo="Carregando chamados..." />
      ) : isError ? (
        <EstadoErro aoTentarDeNovo={() => void refetch()} />
      ) : data.itens.length === 0 ? (
        <EstadoVazio
          icone={Headset}
          titulo={aba === 'fila' ? 'A fila está vazia' : 'Nenhum chamado por aqui'}
          descricao={
            aba === 'fila' ? 'Nenhum chamado esperando atendimento no momento.' : undefined
          }
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-brand/15">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Assunto</TableHead>
                  <TableHead>Ambiente</TableHead>
                  <TableHead>Urgência</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Atendente</TableHead>
                  <TableHead>Aberto</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.itens.map((chamado) => (
                  <TableRow
                    key={chamado.id}
                    onClick={() => navigate(rotaDoChamadoAdmin(chamado.id))}
                    className={cn(
                      'cursor-pointer',
                      // Crítico ganha uma barra à esquerda: numa tabela de oito
                      // colunas, cor de texto no badge não salta o suficiente.
                      chamado.urgencia === 'CRITICA' && 'border-l-2 border-l-destructive',
                    )}
                  >
                    <TableCell className="font-mono text-xs">{chamado.codigo}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{chamado.titulo}</span>
                        {chamado.naoLidas > 0 && (
                          <span className="rounded-full bg-brand px-1.5 py-0.5 text-[10px] font-bold text-brand-foreground">
                            {chamado.naoLidas}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{chamado.solicitanteNome}</p>
                    </TableCell>
                    <TableCell className="text-sm">{chamado.tenantNome}</TableCell>
                    <TableCell>
                      <span className={cn('rounded border px-2 py-0.5 text-xs', CLASSE_URGENCIA[chamado.urgencia])}>
                        {ROTULO_URGENCIA[chamado.urgencia]}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={cn('rounded border px-2 py-0.5 text-xs', CLASSE_STATUS[chamado.status])}>
                        {ROTULO_STATUS_ADMIN[chamado.status]}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {chamado.atendenteNome ?? '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatarRelativo(chamado.abertoEm)}
                    </TableCell>
                    <TableCell>
                      {chamado.status === 'ABERTO' && (
                        <Button
                          size="sm"
                          // A linha inteira navega — sem isto, "Assumir" abriria
                          // o chamado em vez de pegá-lo.
                          onClick={(e) => {
                            e.stopPropagation();
                            void aoAssumir(chamado.id);
                          }}
                          disabled={assumindoId === chamado.id}
                        >
                          {assumindoId === chamado.id && (
                            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                          )}
                          Assumir
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <Paginacao
            pagina={data.pagina}
            totalPaginas={data.totalPaginas}
            total={data.total}
            porPagina={data.porPagina}
            aoMudarPagina={setPagina}
          />
        </>
      )}
    </div>
  );
}

function Cartao({
  rotulo,
  valor,
  destaque,
}: {
  rotulo: string;
  valor: number | undefined;
  destaque?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-lg border p-4',
        destaque && valor ? 'border-amber-600/40 bg-amber-600/5' : 'border-brand/15',
      )}
    >
      <p className="text-xs text-muted-foreground">{rotulo}</p>
      <p className="text-2xl font-semibold">{valor ?? '—'}</p>
    </div>
  );
}
